import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { api } from "../singleton/axios";

export interface WatchlistEarningsEntry {
  id: string;
  ticker: string;
  companyName: string;
  logo: string;
  marketCap: number;
  exchange: string;
  nextEarningsDate: string | null;
  daysUntilEarnings: number | null;
  hasUpcomingEarnings: boolean;
  epsEstimated: number | null;
  revenueEstimated: number | null;
  /** Reporting currency for the estimates above. */
  currency: string | null;
  /** The date lookup failed, as opposed to the company having nothing due. */
  earningsUnavailable: boolean;
  createdAt: string;
}

export interface WatchlistEarningsResponse {
  data: WatchlistEarningsEntry[];
  meta: {
    total: number;
  };
}

interface ApiError {
  success: false;
  error: string;
  message?: string;
  details?: string;
}

// No watchlistId: every list the user owns (the API's own default).
const fetchWatchlistEarnings = async (
  watchlistId: string | null,
): Promise<WatchlistEarningsResponse> => {
  const { data } = await api.get<WatchlistEarningsResponse>("/watchlist-earnings", {
    params: watchlistId === null ? {} : { watchlistId },
  });
  return data;
};

/**
 * Upcoming earnings for one watchlist, already sorted soonest-first by the
 * server (companies with no known date last).
 *
 * This replaces the old pairing of `/earnings-watchlist` with one
 * `/next-earnings` request per ticker — a 20-stock list cost 21 round trips and
 * could not be ordered until the last one resolved, so rows visibly reshuffled
 * after paint. Unlike the dashboard equivalent there is no polling: earnings
 * dates move on an hourly cadence, not a ten-second one.
 */
export const useWatchlistEarnings = (
  watchlistId: string | null,
  { allLists = false }: { allLists?: boolean } = {},
) => {
  const queryClient = useQueryClient();
  const scope = allLists ? null : watchlistId;

  return useQuery<WatchlistEarningsResponse, ApiError>({
    queryKey: ["watchlist-earnings", allLists ? "all" : watchlistId],
    queryFn: async () => {
      try {
        return await fetchWatchlistEarnings(scope);
      } catch (error) {
        // A 404 means the list was deleted on another tab or device. Refreshing
        // the lists lets the selection fall back to a live one immediately.
        if ((error as AxiosError)?.response?.status === 404) {
          queryClient.invalidateQueries({ queryKey: ["watchlists"] });
        }
        throw error;
      }
    },
    enabled: allLists || !!watchlistId,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    // A deleted watchlist won't come back by retrying the same dead id.
    retry: (failureCount, error) =>
      (error as unknown as AxiosError)?.response?.status === 404 ? false : failureCount < 3,
    // Switching lists dims the outgoing rows instead of blanking the screen.
    placeholderData: (previousData) => previousData,
  });
};
